import type { RuntimeMediaDelivery, RuntimeMediaFamily, RuntimeMediaManifest } from "./runtimeConfig";

const SHA256 = /^[a-f0-9]{64}$/;
const RUNTIME_ASSET = /^asset\.[a-z0-9]{1,12}$/;
const SOURCE_PREFIXES: Readonly<Record<RuntimeMediaFamily, readonly string[]>> = {
  image: ["/artworks/images/"],
  audio: ["/artworks/sounds/", "/sounds/"],
  video: ["/artworks/videos/"],
  model3d: ["/artworks/3dmodels/"],
};
const RUNTIME_CONTENT_TYPES: Readonly<
  Record<RuntimeMediaFamily, Readonly<Record<string, string>>>
> = {
  image: {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    avif: "image/avif",
  },
  audio: {
    mp3: "audio/mpeg",
    m4a: "audio/mp4",
    aac: "audio/aac",
    wav: "audio/wav",
    ogg: "audio/ogg",
    oga: "audio/ogg",
    flac: "audio/flac",
  },
  video: {
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
  },
  model3d: {
    glb: "model/gltf-binary",
    stl: "model/stl",
    ply: "application/octet-stream",
  },
};

const runtimeContentType = (filename: string, family: RuntimeMediaFamily): string | null => {
  const extension = filename.toLowerCase().match(/\.([a-z0-9]{1,12})$/)?.[1];
  return extension ? RUNTIME_CONTENT_TYPES[family][extension] || null : null;
};

const normalizeBaseUrl = (value: string): URL | null => {
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:"
      || url.username
      || url.password
      || url.search
      || url.hash
      || url.pathname !== "/"
    ) return null;
    return url;
  } catch (_error) {
    return null;
  }
};

const isPositiveInteger = (value: unknown): value is number => (
  typeof value === "number" && Number.isSafeInteger(value) && value > 0
);

const hasSafePathSegments = (pathname: string): boolean => {
  if (pathname.includes("\\")) return false;
  return pathname.split("/").filter(Boolean).every((segment) => {
    try {
      const decoded = decodeURIComponent(segment);
      return decoded !== "."
        && decoded !== ".."
        && !decoded.includes("/")
        && !decoded.includes("\\")
        && !/[\u0000-\u001f\u007f]/u.test(decoded);
    } catch (_error) {
      return false;
    }
  });
};

const normalizeLegacyOwnedHash = (
  value: string,
  source: URL,
  family: RuntimeMediaFamily,
): URL | null => {
  if (!source.hash) return source;
  if (runtimeContentType(source.pathname, family)) return null;
  const hashIndex = value.indexOf("#");
  if (hashIndex < 0) return null;

  try {
    const normalized = new URL(
      `${value.slice(0, hashIndex)}%23${value.slice(hashIndex + 1).replace(/#/g, "%23")}`,
    );
    return runtimeContentType(normalized.pathname, family) ? normalized : null;
  } catch (_error) {
    return null;
  }
};

export const resolveRuntimeMediaUrl = (
  value: string | null | undefined,
  family: RuntimeMediaFamily,
  delivery?: RuntimeMediaDelivery | null,
  manifest?: RuntimeMediaManifest | null,
): string | null => {
  if (!value) return null;
  if (!delivery?.families.includes(family)) return value;
  const canonicalBase = normalizeBaseUrl(delivery.canonicalDomain);
  const cdnBase = normalizeBaseUrl(delivery.cdnDomain);
  if (!canonicalBase || !cdnBase || !manifest) return value;
  if (
    manifest.schema !== "artetra.runtimeMedia.v1"
    || manifest.family !== family
    || manifest.source.url !== value
    || manifest.source.sha256 !== manifest.asset.sha256
    || !SHA256.test(manifest.asset.sha256)
    || !isPositiveInteger(manifest.asset.byteLength)
    || typeof manifest.asset.contentType !== "string"
    || !manifest.asset.contentType.trim()
  ) return value;

  try {
    const parsedSource = new URL(value);
    const source = normalizeLegacyOwnedHash(value, parsedSource, family);
    const asset = new URL(manifest.asset.url);
    const segments = asset.pathname.split("/");
    if (
      !source
      || source.protocol !== "https:"
      || source.username
      || source.password
      || source.search
      || source.hash
      || source.origin !== canonicalBase.origin
      || !hasSafePathSegments(source.pathname)
      || !SOURCE_PREFIXES[family].some((prefix) => source.pathname.startsWith(prefix))
      || asset.protocol !== "https:"
      || asset.username
      || asset.password
      || asset.search
      || asset.hash
      || asset.origin !== cdnBase.origin
      || segments.length !== 6
      || segments[1] !== "artworks"
      || segments[2] !== "runtime"
      || segments[3] !== family
      || segments[4] !== manifest.asset.sha256
      || !RUNTIME_ASSET.test(segments[5] || "")
      || runtimeContentType(segments[5] || "", family) !== manifest.asset.contentType
    ) return value;
    return asset.toString();
  } catch (_error) {
    return value;
  }
};
